/**
 * Memorial Moments Manager - Admin Component
 * Manages 3 photo-to-animation moment pairs for the Memorial page
 * Includes toggle switches for enabling/disabling sections
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { 
  Upload, AlertCircle, CheckCircle, RefreshCw, 
  Film, Image, Trash2, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTO_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_VIDEO_SIZE = 500 * 1024 * 1024; // 500MB

/**
 * Single Moment Manager Card
 */
const MomentCard = ({ slot, moment, token, onRefresh }) => {
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  
  // Photo upload state
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoProgress, setPhotoProgress] = useState(0);
  const [photoError, setPhotoError] = useState(null);
  
  // Video upload state
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoError, setVideoError] = useState(null);
  const [uploadPhase, setUploadPhase] = useState('');
  
  // Delete state
  const [deletePhotoConfirm, setDeletePhotoConfirm] = useState(false);
  const [deleteVideoConfirm, setDeleteVideoConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Handle photo upload (simple upload)
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      setPhotoError('Please select an image file (JPEG, PNG, WebP)');
      return;
    }
    
    if (file.size > MAX_PHOTO_SIZE) {
      setPhotoError(`Photo too large. Max size: ${MAX_PHOTO_SIZE / (1024*1024)}MB`);
      return;
    }
    
    setPhotoUploading(true);
    setPhotoProgress(0);
    setPhotoError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/memorial-moments/photo/${slot}`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setPhotoProgress(percent);
        }
      });
      
      setPhotoProgress(100);
      onRefresh();
    } catch (err) {
      console.error('[MOMENT-PHOTO] Upload failed:', err);
      setPhotoError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setPhotoUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  // Handle video upload (multipart)
  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('video/')) {
      setVideoError('Please select a video file (MP4, WebM)');
      return;
    }
    
    if (file.size > MAX_VIDEO_SIZE) {
      setVideoError(`Video too large. Max size: ${MAX_VIDEO_SIZE / (1024*1024)}MB`);
      return;
    }
    
    setVideoUploading(true);
    setVideoProgress(0);
    setVideoError(null);
    setUploadPhase('Initiating...');
    
    try {
      // Step 1: Initiate multipart upload
      const initRes = await axios.post(`${API}/memorial-moments/multipart/initiate/${slot}`, {
        slot,
        filename: file.name,
        fileSize: file.size,
        contentType: file.type
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const { uploadId, objectKey, partSize } = initRes.data;
      const parts = [];
      const totalParts = Math.ceil(file.size / partSize);
      
      setUploadPhase(`Uploading ${totalParts} parts...`);
      
      // Step 2: Upload parts via proxy
      for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
        const start = (partNumber - 1) * partSize;
        const end = Math.min(start + partSize, file.size);
        const chunk = file.slice(start, end);
        
        const partRes = await axios.put(
          `${API}/memorial-moments/multipart/upload-part/${uploadId}/${partNumber}?key=${encodeURIComponent(objectKey)}`,
          chunk,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/octet-stream'
            }
          }
        );
        
        parts.push({
          PartNumber: partRes.data.PartNumber,
          ETag: partRes.data.ETag
        });
        
        const progress = Math.round((partNumber / totalParts) * 90);
        setVideoProgress(progress);
        setUploadPhase(`Part ${partNumber}/${totalParts}`);
      }
      
      // Step 3: Complete multipart upload
      setUploadPhase('Completing...');
      setVideoProgress(95);
      
      await axios.post(`${API}/memorial-moments/multipart/complete/${uploadId}`, {
        uploadId,
        objectKey,
        slot,
        parts
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setVideoProgress(100);
      setUploadPhase('Done!');
      onRefresh();
    } catch (err) {
      console.error('[MOMENT-VIDEO] Upload failed:', err);
      setVideoError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setVideoUploading(false);
      setUploadPhase('');
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  };

  // Delete photo
  const handleDeletePhoto = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/memorial-moments/photo/${slot}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDeletePhotoConfirm(false);
      onRefresh();
    } catch (err) {
      setPhotoError(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  // Delete video
  const handleDeleteVideo = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/memorial-moments/video/${slot}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDeleteVideoConfirm(false);
      onRefresh();
    } catch (err) {
      setVideoError(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return null;
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="bg-zinc-900/50 rounded-xl p-5 border border-zinc-800">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-purple-400" />
        </div>
        <div>
          <h3 className="text-white font-semibold">Moment {slot}</h3>
          <p className="text-zinc-500 text-xs">Photo + Animation pair</p>
        </div>
      </div>
      
      {/* Two-column layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        
        {/* Photo Section */}
        <div className="bg-zinc-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Image className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-medium text-zinc-300">Still Photo</span>
          </div>
          
          {moment.hasPhoto && moment.photoUrl ? (
            <div className="relative mb-3">
              <img
                src={moment.photoUrl}
                alt={`Moment ${slot} photo`}
                className="w-full aspect-video rounded-lg object-cover bg-black"
              />
              {!deletePhotoConfirm ? (
                <button
                  onClick={() => setDeletePhotoConfirm(true)}
                  className="absolute top-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 rounded-full transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5 text-white" />
                </button>
              ) : (
                <div className="absolute inset-0 bg-black/80 rounded-lg flex items-center justify-center">
                  <div className="text-center p-4">
                    <p className="text-white text-sm mb-3">Delete this photo?</p>
                    <div className="flex gap-2 justify-center">
                      <button onClick={handleDeletePhoto} disabled={deleting} className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg disabled:opacity-50">
                        {deleting ? 'Deleting...' : 'Delete'}
                      </button>
                      <button onClick={() => setDeletePhotoConfirm(false)} className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-white text-sm rounded-lg">Cancel</button>
                    </div>
                  </div>
                </div>
              )}
              {moment.photoFilename && <p className="text-xs text-zinc-500 mt-2 truncate">{moment.photoFilename}</p>}
              {moment.photoUpdatedAt && <p className="text-xs text-zinc-600">{formatDate(moment.photoUpdatedAt)}</p>}
            </div>
          ) : (
            <div className="aspect-video bg-zinc-700/30 rounded-lg flex items-center justify-center mb-3">
              <div className="text-center text-zinc-500">
                <Image className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-xs">No photo</p>
              </div>
            </div>
          )}
          
          <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePhotoUpload} className="hidden" />
          
          {photoUploading ? (
            <div className="space-y-2">
              <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
                <div className="h-full bg-purple-500 transition-all" style={{ width: `${photoProgress}%` }} />
              </div>
              <p className="text-xs text-zinc-400 text-center">{photoProgress}%</p>
            </div>
          ) : (
            <button onClick={() => photoInputRef.current?.click()} className="w-full py-2 px-3 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg text-sm flex items-center justify-center gap-2 transition-colors">
              <Upload className="w-4 h-4" />
              {moment.hasPhoto ? 'Replace Photo' : 'Upload Photo'}
            </button>
          )}
          
          {photoError && <p className="text-red-400 text-xs mt-2 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {photoError}</p>}
        </div>
        
        {/* Video Section */}
        <div className="bg-zinc-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Film className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-medium text-zinc-300">Animation Video</span>
          </div>
          
          {moment.hasVideo && moment.videoUrl ? (
            <div className="relative mb-3">
              <video src={moment.videoUrl} className="w-full aspect-video rounded-lg object-cover bg-black" controls muted preload="metadata" />
              {!deleteVideoConfirm ? (
                <button onClick={() => setDeleteVideoConfirm(true)} className="absolute top-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 rounded-full transition-colors">
                  <Trash2 className="w-3.5 h-3.5 text-white" />
                </button>
              ) : (
                <div className="absolute inset-0 bg-black/80 rounded-lg flex items-center justify-center">
                  <div className="text-center p-4">
                    <p className="text-white text-sm mb-3">Delete this video?</p>
                    <div className="flex gap-2 justify-center">
                      <button onClick={handleDeleteVideo} disabled={deleting} className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg disabled:opacity-50">
                        {deleting ? 'Deleting...' : 'Delete'}
                      </button>
                      <button onClick={() => setDeleteVideoConfirm(false)} className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-white text-sm rounded-lg">Cancel</button>
                    </div>
                  </div>
                </div>
              )}
              {moment.videoFilename && <p className="text-xs text-zinc-500 mt-2 truncate">{moment.videoFilename}</p>}
              {moment.videoUpdatedAt && <p className="text-xs text-zinc-600">{formatDate(moment.videoUpdatedAt)}</p>}
            </div>
          ) : (
            <div className="aspect-video bg-zinc-700/30 rounded-lg flex items-center justify-center mb-3">
              <div className="text-center text-zinc-500">
                <Film className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-xs">No video</p>
              </div>
            </div>
          )}
          
          <input ref={videoInputRef} type="file" accept="video/mp4,video/webm,video/quicktime" onChange={handleVideoUpload} className="hidden" />
          
          {videoUploading ? (
            <div className="space-y-2">
              <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
                <div className="h-full bg-purple-500 transition-all" style={{ width: `${videoProgress}%` }} />
              </div>
              <p className="text-xs text-zinc-400 text-center">{uploadPhase} {videoProgress}%</p>
            </div>
          ) : (
            <button onClick={() => videoInputRef.current?.click()} className="w-full py-2 px-3 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg text-sm flex items-center justify-center gap-2 transition-colors">
              <Upload className="w-4 h-4" />
              {moment.hasVideo ? 'Replace Video' : 'Upload Video'}
            </button>
          )}
          
          {videoError && <p className="text-red-400 text-xs mt-2 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {videoError}</p>}
        </div>
      </div>
      
      {/* Status indicator */}
      <div className="mt-4 flex items-center justify-center gap-2 text-xs">
        {moment.hasPhoto && moment.hasVideo ? (
          <span className="flex items-center gap-1 text-green-400"><CheckCircle className="w-3.5 h-3.5" /> Ready to display</span>
        ) : moment.hasPhoto ? (
          <span className="flex items-center gap-1 text-yellow-400"><AlertCircle className="w-3.5 h-3.5" /> Needs animation video</span>
        ) : moment.hasVideo ? (
          <span className="flex items-center gap-1 text-yellow-400"><AlertCircle className="w-3.5 h-3.5" /> Will use video poster as photo</span>
        ) : (
          <span className="flex items-center gap-1 text-zinc-500"><AlertCircle className="w-3.5 h-3.5" /> Upload photo to start</span>
        )}
      </div>
    </div>
  );
};

/**
 * Memorial Moments Manager
 */
const MemorialMomentsManager = ({ token }) => {
  const [data, setData] = useState({ settings: {}, moments: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/memorial-moments/admin`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setData(res.data || { settings: {}, moments: [] });
      setError(null);
    } catch (err) {
      console.error('[MEMORIAL-MOMENTS] Failed to fetch:', err);
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        setError('unauthorized');
      } else {
        const detail = err.response?.data?.detail || err.message || 'Unknown error';
        setError(`Endpoint: GET /api/memorial-moments/admin | Status: ${status || 'network'} | ${detail}`);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-purple-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    if (error === 'unauthorized') {
      return (
        <div className="text-center py-8" data-testid="moments-unauthorized">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
          <p className="text-red-400 font-medium">You are not authorized</p>
          <p className="text-zinc-500 text-sm mt-1">Admin access required to manage moments.</p>
        </div>
      );
    }
    return (
      <div className="text-center py-8" data-testid="moments-error">
        <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
        <p className="text-red-400 text-sm font-mono bg-zinc-900 rounded-lg p-3 inline-block text-left max-w-lg">{error}</p>
        <div className="mt-4">
          <button onClick={fetchData} className="px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg text-sm">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { moments } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-400" />
            Manage Moments
          </h2>
          <p className="text-zinc-400 text-sm mt-1">
            Upload photo-to-animation pairs for the carousel
          </p>
        </div>
        <button onClick={fetchData} className="p-2 hover:bg-zinc-800 rounded-lg transition-colors" title="Refresh">
          <RefreshCw className="w-5 h-5 text-zinc-400" />
        </button>
      </div>
      
      {/* Moment Cards */}
      <div className="space-y-4">
        {moments.map((moment) => (
          <MomentCard
            key={moment.slot}
            slot={moment.slot}
            moment={moment}
            token={token}
            onRefresh={fetchData}
          />
        ))}
      </div>
      
      {/* Instructions */}
      <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/20">
        <h3 className="text-purple-400 font-medium mb-2">How it works</h3>
        <ul className="text-zinc-400 text-sm space-y-1">
          <li>• Each moment shows as a still photo on the Memorial page</li>
          <li>• When visitors tap the photo, it animates into the video</li>
          <li>• Videos play muted and loop until the visitor navigates away</li>
          <li>• If only video is uploaded, its first frame will be used as the still</li>
          <li>• Use the toggles above to show/hide sections instantly</li>
        </ul>
      </div>
    </div>
  );
};

export default MemorialMomentsManager;
